import { EnrichmentResult } from './types';

const CSV_DELIMITER = ',';

/** Columns shown in the ORA results table, plus identifiers useful in the download. */
export const ENRICHMENT_CSV_COLUMNS = [
  'diseaseUMLSCUI',
  'diseaseName',
  'diseaseClassName_MSH',
  'pvalue',
  'fdrQValue',
  'negLog10P',
  'bgRatio',
  'oddsRatio',
  'geneRatio',
  'variantRatio',
  'intersectionSize',
  'intersection',
  'score',
  'source',
] as const;

const PREFERRED_COLUMN_SET = new Set<string>(ENRICHMENT_CSV_COLUMNS);

const FDR_ALIASES = ['fdrQValue', 'fdrQvalue', 'qvalue'] as const;

export const escapeCsvValue = (value: unknown, delimiter = CSV_DELIMITER): string => {
  if (value === null || value === undefined) return '';

  if (Array.isArray(value)) {
    return escapeCsvValue(value.map(item => String(item)).join(';'), delimiter);
  }

  if (typeof value === 'object') {
    return escapeCsvValue(JSON.stringify(value), delimiter);
  }

  const str = String(value);
  if (str.includes(delimiter) || /["\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }

  return str;
};

const toNegLog10P = (pvalue: unknown): number | '' => {
  const p = Number(pvalue);
  if (!Number.isFinite(p) || p <= 0) return '';
  return -Math.log10(p);
};

const getFdrQValue = (item: EnrichmentResult): unknown => {
  for (const key of FDR_ALIASES) {
    const value = item[key];
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return item.fdrQValue;
};

const getColumnValue = (item: EnrichmentResult, column: string): unknown => {
  if (column === 'negLog10P') return toNegLog10P(item.pvalue);
  if (column === 'fdrQValue') return getFdrQValue(item);
  return item[column];
};

const extraColumnsFromResults = (results: EnrichmentResult[]): string[] => {
  const extras = new Set<string>();

  results.forEach(item => {
    Object.keys(item).forEach(key => {
      if (PREFERRED_COLUMN_SET.has(key)) return;
      if ((FDR_ALIASES as readonly string[]).includes(key)) return;
      extras.add(key);
    });
  });

  return [...extras].sort();
};

export const buildEnrichmentResultsCsv = (results: EnrichmentResult[] | null | undefined): string => {
  if (!results || results.length === 0) return '';

  const headers = [...ENRICHMENT_CSV_COLUMNS, ...extraColumnsFromResults(results)];
  const rows = results.map(item =>
    headers.map(column => escapeCsvValue(getColumnValue(item, column))).join(CSV_DELIMITER),
  );

  return [headers.join(CSV_DELIMITER), ...rows].join('\n');
};
